import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
  useContext,
} from "react";
import PropTypes from "prop-types";
import { Paginator } from "primereact/paginator";
import { AdvanceFilter } from "../../M_Components/M_Table/advanceFilter/AdvanceFilter";
import { MCheckBox } from "src/M_Components";

import { useTranslation } from "react-i18next";
import {
  getCellColor,
  headerCellStyle,
  cellStyle,
} from "../tableHelpers/TableStyleUtils";
import { exportGroupedExcel } from "../tableHelpers/TableExcelUtils";
import TableHeader from "../tableHelpers/TableHeader";
import ColumnMenu from "../tableHelpers/ColumnMenu";
import ViewsMenu from "../tableHelpers/ViewMenu";

import { LoadingContext } from "src/M_Components";
import "./CarrierResponseDetailTable.css";

/**
 * Reads a numeric value from a quote object, normalizing both PascalCase and camelCase
 * so the component can work with different DTO shapes.
 *
 * @param {object} quote - Quote DTO for a single carrier/lane.
 * @param {"bid"|"loads"|"etd"} field - Logical field name we want to read.
 * @returns {number|null} Normalized numeric value or null when absent.
 */
const getQuoteValue = (quote, field) => {
  if (!quote) return null;

  switch (field) {
    case "bid":
      // Bid can arrive as BidValue or bidValue
      return quote.BidValue ?? quote.bidValue ?? null;
    case "loads":
      // Loads can arrive as Loads or loads
      return quote.Loads ?? quote.loads ?? null;
    case "etd":
      // Estimated transit days can arrive as EstimatedTransitDays or estimatedTransitDays
      return quote.EstimatedTransitDays ?? quote.estimatedTransitDays ?? null;
    default:
      return null;
  }
};

/**
 * Returns the normalized lane id from a lane DTO.
 */
const getLaneId = (lane) => {
  if (!lane) return null;
  return (
    lane.LaneId ??
    lane.laneId ??
    lane.Id ??
    (lane.lane && (lane.lane.id ?? lane.laneId)) ??
    null
  );
};

/**
 * Returns the normalized source name from a lane DTO.
 */
const getLaneSource = (lane) => {
  if (!lane) return "";
  return (
    lane.LaneSource ??
    lane.laneSource ??
    lane.sourceLocation ??
    lane.Origin ??
    lane.origin ??
    (lane.lane && (lane.lane.sourceLocation ?? lane.lane.LaneSource)) ??
    ""
  );
};

/**
 * Returns the normalized destination name from a lane DTO.
 */
const getLaneDestination = (lane) => {
  if (!lane) return "";
  return (
    lane.LaneDestination ??
    lane.laneDestination ??
    lane.destinationLocation ??
    lane.Destination ??
    lane.destination ??
    (lane.lane &&
      (lane.lane.destinationLocation ?? lane.lane.LaneDestination)) ??
    ""
  );
};

/**
 * Returns true when a lane is considered active.
 */
const isLaneActive = (lane) => {
  if (!lane) return false;

  // Prefer explicit IsActive flags when present
  if (lane.IsActive !== undefined) return !!lane.IsActive;
  if (lane.isActive !== undefined) return !!lane.isActive;

  // Fallback to inactive flags on relation or nested lane
  const relInactive = lane.Inactive ?? lane.inactive;
  const nestedInactive =
    lane.lane && (lane.lane.Inactive ?? lane.lane.inactive);

  if (relInactive !== undefined) return !relInactive;
  if (nestedInactive !== undefined) return !nestedInactive;

  // If nothing is defined, assume active
  return true;
};

/**
 * Reads the current acceptance state from a quote object.
 */
const getQuoteAccepted = (quote) =>
  quote?.IsAccepted ?? quote?.isAccepted ?? false;

/**
 * Reads the QuoteId (or Id) from a quote object.
 */
const getQuoteId = (quote) =>
  quote?.QuoteId ?? quote?.quoteId ?? quote?.Id ?? quote?.id ?? null;

/**
 * CarrierResponseDetailTable
 *
 * This component renders a flattened, lane-based table with response details
 * for the currently selected carrier. Each row represents a lane
 * (Origin x Destination) and the metric cells show:
 *  - Best value across all carriers for that lane, based on the selected metric
 *  - The current carrier's Bid, Loads and Estimated Transit Days
 *
 * The component supports:
 *  - Advanced filtering by Origin and Destination
 *  - Customizable visible columns
 *  - Saved "views" for reusing filter configurations
 *  - Export to Excel using the same flattened structure
 *  - Row-level coloring for the chosen metric (bid, loads or etd)
 *
 * It also supports selecting/unselecting a quote via the "Select Quote" column:
 *  - Spot tenders: always enabled
 *  - Non-Spot tenders: enabled only when the selected phase is the last phase
 */
export default function CarrierResponseDetailTable({
  tenderInfo,
  selectedCarrier,
  selectedPhaseId,
  quotes,
  lanes,
  isSpotTender,
  onRefresh,
  bestMetric = "bid", // accepted values: 'bid' | 'loads' | 'etd'

  // Baseline best values per lane computed from the full population of quotes.
  // This allows the coloring logic to stay consistent even when the table is fed with a subset.
  laneBestMap,

  // Busy ids and accept handler are provided by the page so all views stay in sync.
  acceptBusyIds,
  onSetQuoteAccepted,
}) {
  const { t } = useTranslation();

  // Context provides DEFAULTURL and toastMessages
  const ctx = useContext(LoadingContext);

  // Keep toast function in a ref to avoid stale closures
  const toastRef = useRef(ctx?.toastMessages);

  useEffect(() => {
    toastRef.current = ctx?.toastMessages;
  }, [ctx?.toastMessages]);

  // Pagination state
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  // Anchors for popover / menu components
  const [anchorEls, setAnchorEls] = useState(null); // column selector
  const [anchorV, setAnchorV] = useState(null); // views selector
  const [anchorAF, setAnchorAF] = useState(null); // advanced filter

  // Advanced filter state
  const [metaPair, setMetaPair] = useState([]);

  // Saved views state (name -> serialized filters)
  const [views, setViews] = useState(new Map([["Default", ""]]));
  const [selectedView, setSelectedView] = useState("Default");

  //============================================================================================================
  // Metadata definition for AdvancedFilter
  // - Describes which fields can be filtered and their data types
  //============================================================================================================
  const metaType = useMemo(
    () => [
      {
        metaName: "destination", // internal filter name used in metaPair
        metaDisplay: "Destination", // label shown in the filter UI
        metaType: "string",
        metaIsList: false,
      },
      {
        metaName: "origin",
        metaDisplay: "Origin",
        metaType: "string",
        metaIsList: false,
      },
    ],
    []
  );

  //============================================================================================================
  // Adds or replaces a filter in the metaPair collection
  //============================================================================================================
  const addMetaPair = (filter) => {
    setMetaPair((prev) => {
      const exists = prev.find((f) => f.name === filter.name);
      return exists
        ? prev.map((f) => (f.name === filter.name ? filter : f))
        : [...prev, filter];
    });
  };

  //============================================================================================================
  // Removes a filter from metaPair by its name
  //============================================================================================================
  const deleteFilter = (filterName) => {
    setMetaPair((prev) => prev.filter((f) => f.name !== filterName));
  };

  //============================================================================================================
  // Clears all filters at once
  //============================================================================================================
  const deleteAllFilters = () => {
    setMetaPair([]);
  };

  //============================================================================================================
  // Applies filters and resets pagination to the first page
  //============================================================================================================
  const submitFilters = () => {
    setFirst(0);
  };

  //============================================================================================================
  // Filters lanes to only keep active ones
  //============================================================================================================
  const activeLanes = useMemo(
    () => (lanes || []).filter((l) => isLaneActive(l)),
    [lanes]
  );

  //============================================================================================================
  // Filters quotes by TenderPhase for non-spot tenders
  // - Also removes inactive quotes
  //============================================================================================================
  const filteredQuotes = useMemo(() => {
    const activeQuotes = (quotes || []).filter((q) => {
      const inactiveFlag = q.Inactive ?? q.inactive;
      return inactiveFlag === undefined || inactiveFlag === false;
    });

    if (isSpotTender) return activeQuotes;

    return activeQuotes.filter((q) => {
      const phaseId = q.TenderPhaseId ?? q.tenderPhaseId;
      return selectedPhaseId != null ? phaseId === selectedPhaseId : true;
    });
  }, [quotes, isSpotTender, selectedPhaseId]);

  //============================================================================================================
  // Determines if quote selection should be enabled:
  // - Spot tenders: always enabled
  // - Non-spot tenders: enabled only for the last phase
  //============================================================================================================
  const isLastPhaseSelected = useMemo(() => {
    if (isSpotTender) return true;

    // Prefer reading IsLastPhase from tenderInfo phases when available
    const phases =
      tenderInfo?.Phases ??
      tenderInfo?.phases ??
      tenderInfo?.TenderPhases ??
      tenderInfo?.tenderPhases ??
      [];

    if (Array.isArray(phases) && selectedPhaseId != null) {
      const p = phases.find((x) => {
        const id = x?.Id ?? x?.id;
        return id === selectedPhaseId;
      });

      const flag = p?.IsLastPhase ?? p?.isLastPhase;
      if (flag !== undefined && flag !== null) return flag === true;
    }

    // Fallback: attempt to infer from quote payload
    const match = (quotes || []).find((q) => {
      const phaseId = q.TenderPhaseId ?? q.tenderPhaseId;
      return selectedPhaseId != null ? phaseId === selectedPhaseId : false;
    });

    const phaseObj = match?.TenderPhase ?? match?.tenderPhase ?? null;
    const flag = phaseObj?.IsLastPhase ?? phaseObj?.isLastPhase ?? null;

    return flag === true;
  }, [isSpotTender, tenderInfo, quotes, selectedPhaseId]);

  const quoteSelectionEnabled = isSpotTender || isLastPhaseSelected;

  const quoteSelectionDisabledReason = useMemo(() => {
    if (quoteSelectionEnabled) return "";
    return (
      t(
        "Quote selection is only allowed in the last phase for Non-Spot tenders."
      ) ||
      "Quote selection is only allowed in the last phase for Non-Spot tenders."
    );
  }, [quoteSelectionEnabled, t]);

  //============================================================================================================
  // Applies advanced filters to the list of active lanes
  //============================================================================================================
  const filteredLanes = useMemo(() => {
    let data = [...activeLanes];

    metaPair.forEach((filter) => {
      if (!filter.value) return;

      const value = String(filter.value).toLowerCase();
      let fieldName = null;

      // Map filter names to lane DTO fields
      if (filter.name === "destination") fieldName = "destination";
      if (filter.name === "origin") fieldName = "origin";

      if (!fieldName) return;

      data = data.filter((l) => {
        const fieldVal =
          fieldName === "origin"
            ? String(getLaneSource(l)).toLowerCase()
            : String(getLaneDestination(l)).toLowerCase();

        if (filter.operation === "Contains") {
          return fieldVal.includes(value);
        }

        if (filter.operation === "Equals") {
          return fieldVal === value;
        }

        return true;
      });
    });

    return data;
  }, [activeLanes, metaPair]);

  //============================================================================================================
  // Slices the filtered lanes according to current pagination state
  //============================================================================================================
  const pagedLanes = useMemo(() => {
    return filteredLanes.slice(first, first + rows);
  }, [filteredLanes, first, rows]);

  //============================================================================================================
  // Computes all metrics for a given lane
  // - Best values are resolved from laneBestMap when provided, so the baseline can include all quotes.
  //============================================================================================================
  const getLaneMetrics = useCallback(
    (laneId) => {
      const laneQuotes = filteredQuotes.filter((q) => {
        const qLaneId = q.LaneId ?? q.laneId;
        return qLaneId === laneId;
      });

      const bidValues = laneQuotes
        .map((q) => getQuoteValue(q, "bid"))
        .filter((v) => v != null);
      const loadsValues = laneQuotes
        .map((q) => getQuoteValue(q, "loads"))
        .filter((v) => v != null);
      const etdValues = laneQuotes
        .map((q) => getQuoteValue(q, "etd"))
        .filter((v) => v != null);

      const localBestBidValue =
        bidValues.length > 0 ? Math.min(...bidValues) : null;
      const localBestLoadsValue =
        loadsValues.length > 0 ? Math.max(...loadsValues) : null;
      const localBestEtdValue =
        etdValues.length > 0 ? Math.min(...etdValues) : null;

      const global =
        laneBestMap && laneId != null ? laneBestMap.get(laneId) : null;

      const bestBidValue =
        global?.bestBidValue ?? global?.bestBidValue === 0
          ? global.bestBidValue
          : global?.bestBidValue;

      const bestLoadsValue =
        global?.bestLoadsValue ?? global?.bestLoadsValue === 0
          ? global.bestLoadsValue
          : global?.bestLoadsValue;

      const bestEtdValue =
        global?.bestEtdValue ?? global?.bestEtdValue === 0
          ? global.bestEtdValue
          : global?.bestEtdValue;

      const resolvedBestBid = bestBidValue ?? localBestBidValue ?? null;
      const resolvedBestLoads = bestLoadsValue ?? localBestLoadsValue ?? null;
      const resolvedBestEtd = bestEtdValue ?? localBestEtdValue ?? null;

      let bestValue = null;
      switch (bestMetric) {
        case "bid":
          bestValue = resolvedBestBid;
          break;
        case "loads":
          bestValue = resolvedBestLoads;
          break;
        case "etd":
          bestValue = resolvedBestEtd;
          break;
        default:
          bestValue = resolvedBestBid;
          break;
      }

      const carrierQuote = laneQuotes.find((q) => {
        const qCarrierId = q.CarrierId ?? q.carrierId;
        return qCarrierId === selectedCarrier;
      });

      const carrierBidValue = getQuoteValue(carrierQuote, "bid");
      const carrierLoads = getQuoteValue(carrierQuote, "loads");
      const carrierEtd = getQuoteValue(carrierQuote, "etd");

      return {
        bestValue,
        bestBidValue: resolvedBestBid,
        bestLoadsValue: resolvedBestLoads,
        bestEtdValue: resolvedBestEtd,
        carrierBidValue,
        carrierLoads,
        carrierEtd,
      };
    },
    [filteredQuotes, bestMetric, selectedCarrier, laneBestMap]
  );

  //============================================================================================================
  // Resolves the selected carrier quote for a given laneId
  //============================================================================================================
  const getSelectedCarrierQuoteForLane = useCallback(
    (laneId) => {
      const laneQuotes = filteredQuotes.filter((q) => {
        const qLaneId = q.LaneId ?? q.laneId;
        return qLaneId === laneId;
      });

      return (
        laneQuotes.find((q) => {
          const qCarrierId = q.CarrierId ?? q.carrierId;
          return qCarrierId === selectedCarrier;
        }) || null
      );
    },
    [filteredQuotes, selectedCarrier]
  );

  /**
   * Accepts or unaccepts a quote for a given QuoteId.
   *
   * Behaviour:
   * - If the API operation succeeds, the table is refreshed so the new
   *   IsAccepted state is reflected.
   * - If the API returns a controlled failure, a toast is shown with the
   *   message provided by the backend.
   * - If an unexpected error occurs (network, runtime, etc.), a generic
   *   error toast is shown.
   */
  const setQuoteAccepted = useCallback(
    async ({ quoteId, nextAccepted }) => {
      if (!quoteId) return;

      if (typeof onSetQuoteAccepted === "function") {
        onSetQuoteAccepted({ quoteId, nextAccepted });
        return;
      }

      toastRef.current?.(
        "error",
        "",
        t("This action is not available in the current context.") ||
        "This action is not available in the current context."
      );
    },
    [onSetQuoteAccepted, t]
  );

  //============================================================================================================
  // Exports the current filtered lanes to an Excel file
  //============================================================================================================
  const exportExcel = async () => {
    const singles = [
      { label: "Origin" },
      { label: "Destination" },
      { label: "Best" },
      { label: "Bid Value" },
      { label: "Loads" },
      { label: "Estimated Transit Days" },
      { label: "Select Quote" },
    ];

    await exportGroupedExcel({
      sheetName: "CarrierResponses",
      filename: "CarrierResponses.xlsx",
      singles,
      groups: [],
      data: filteredLanes,
      buildRow: (lane) => {
        const laneId = getLaneId(lane);
        const { bestValue, carrierBidValue, carrierLoads, carrierEtd } =
          getLaneMetrics(laneId);

        const carrierQuote = getSelectedCarrierQuoteForLane(laneId);
        const accepted = getQuoteAccepted(carrierQuote);

        return [
          getLaneSource(lane),
          getLaneDestination(lane),
          bestValue != null ? bestValue : "-",
          carrierBidValue != null ? carrierBidValue : "-",
          carrierLoads != null ? carrierLoads : "-",
          carrierEtd != null ? carrierEtd : "-",
          accepted ? "Yes" : "No",
        ];
      },
    });
  };

  //============================================================================================================
  // Column configuration for the table
  //============================================================================================================
  const singleColumns = useMemo(
    () => [
      "Origin",
      "Destination",
      "Best",
      "BidValue",
      "Loads",
      "EstimatedTransitDays",
      "SelectQuote",
    ],
    []
  );

  const allColumnsList = useMemo(() => singleColumns, [singleColumns]);

  //============================================================================================================
  // Controlled state for which columns are currently visible in the table
  //============================================================================================================
  const [selectedColumns, setSelectedColumns] = useState(allColumnsList);

  //============================================================================================================
  // Normalizes acceptBusyIds so it works with either a Set or an array of ids
  //============================================================================================================
  const isBusy = useCallback(
    (quoteId) => {
      if (!quoteId) return false;
      if (!acceptBusyIds) return false;
      if (acceptBusyIds instanceof Set) return acceptBusyIds.has(quoteId);
      if (Array.isArray(acceptBusyIds)) return acceptBusyIds.includes(quoteId);
      return false;
    },
    [acceptBusyIds]
  );

  return (
    <div className="carrier-response-detail-root">
      <div className="carrier-response-detail-header">
        <TableHeader
          filterCount={metaPair.length}
          onRefresh={onRefresh}
          onExport={exportExcel}
          onOpenViews={(e) => setAnchorV(e.currentTarget)}
          onOpenFilters={(e) => setAnchorAF(e.currentTarget)}
          onOpenColumns={(e) => setAnchorEls(e.currentTarget)}
        />
      </div>

      {/* Column selection menu (toggles table columns on and off) */}
      <ColumnMenu
        anchorEl={anchorEls}
        open={Boolean(anchorEls)}
        onClose={() => setAnchorEls(null)}
        singleColumns={singleColumns}
        columnGroups={[]}
        selectedColumns={selectedColumns}
        onChangeSelected={setSelectedColumns}
      />

      {/* Views menu to load / store sets of filters as named "views" */}
      <ViewsMenu
        anchorEl={anchorV}
        open={Boolean(anchorV)}
        onClose={() => setAnchorV(null)}
        viewsMap={views}
        onSelectDefault={() => {
          setSelectedView("Default");
          setMetaPair([]);
          setAnchorV(null);
        }}
        onSelectView={(view) => {
          const str = views.get(view);
          if (str) {
            try {
              setMetaPair(JSON.parse(str));
              setSelectedView(view);
            } catch { }
          }
          setAnchorV(null);
        }}
        onCreateNewViewClick={() => {
          setAnchorV(null);
          setAnchorAF(document.getElementById("advFilter"));
        }}
      />

      {/* Advanced filter popover that edits metaPair using metaType definition */}
      <AdvanceFilter
        metaPair={metaPair}
        metaType={metaType}
        onAddMetaPair={addMetaPair}
        onSubmitFilters={submitFilters}
        onDeleteFilter={deleteFilter}
        onDeleteAllFilters={deleteAllFilters}
        anchorAF={anchorAF}
        setAnchorAF={setAnchorAF}
        onSaveFilter={(viewName) => {
          const filterUrl = JSON.stringify(metaPair);
          setViews(new Map(views.set(viewName, filterUrl)));
          setSelectedView(viewName);
        }}
        views={views}
        onToasts={() => { }}
        color="var(--color-red)"
        removeFilters={["Add"]}
      />

      <div className="carrier-response-detail-table-wrapper">
        {/* Main table with one row per lane (Origin x Destination) */}
        <table className="carrier-response-detail-table">
          <thead>
            <tr>
              {selectedColumns.includes("Origin") && (
                <th style={headerCellStyle()}>{t("Origin")}</th>
              )}
              {selectedColumns.includes("Destination") && (
                <th style={headerCellStyle()}>{t("Destination")}</th>
              )}
              {selectedColumns.includes("Best") && (
                <th style={headerCellStyle()}>{t("BestMetric")}</th>
              )}
              {selectedColumns.includes("BidValue") && (
                <th style={headerCellStyle()}>{t("BidValue")}</th>
              )}
              {selectedColumns.includes("Loads") && (
                <th style={headerCellStyle()}>{t("Loads")}</th>
              )}
              {selectedColumns.includes("EstimatedTransitDays") && (
                <th style={headerCellStyle()}>{t("EstimatedTransitDays")}</th>
              )}
              {selectedColumns.includes("SelectQuote") && (
                <th style={headerCellStyle()}>
                  {t("Select Quote") || "Select Quote"}
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {pagedLanes.map((lane) => {
              const laneId = getLaneId(lane);
              const { bestValue, carrierBidValue, carrierLoads, carrierEtd } =
                getLaneMetrics(laneId);

              const bestBg =
                bestValue != null
                  ? getCellColor(bestValue, bestValue)
                  : "var(--color-grey-light)";

              let rowBg = "var(--color-grey-light)";

              if (bestValue != null) {
                if (bestMetric === "bid" && carrierBidValue != null) {
                  rowBg = getCellColor(carrierBidValue, bestValue);
                } else if (bestMetric === "etd" && carrierEtd != null) {
                  rowBg = getCellColor(carrierEtd, bestValue);
                } else if (bestMetric === "loads" && carrierLoads != null) {
                  rowBg = getCellColor(bestValue, carrierLoads);
                }
              }

              const carrierQuote = getSelectedCarrierQuoteForLane(laneId);
              const quoteId = getQuoteId(carrierQuote);
              const accepted = getQuoteAccepted(carrierQuote);

              const disabled =
                !quoteSelectionEnabled || !quoteId || isBusy(quoteId);

              const tooltip = !quoteSelectionEnabled
                ? quoteSelectionDisabledReason
                : !quoteId
                  ? t("No quote found for this lane.") ||
                  "No quote found for this lane."
                  : "";

              return (
                <tr
                  key={laneId ?? getLaneSource(lane) + getLaneDestination(lane)}
                >
                  {selectedColumns.includes("Origin") && (
                    <td style={cellStyle()}>{getLaneSource(lane)}</td>
                  )}
                  {selectedColumns.includes("Destination") && (
                    <td style={cellStyle()}>{getLaneDestination(lane)}</td>
                  )}
                  {selectedColumns.includes("Best") && (
                    <td style={cellStyle({ background: bestBg })}>
                      {bestValue != null ? bestValue : "-"}
                    </td>
                  )}
                  {selectedColumns.includes("BidValue") && (
                    <td style={cellStyle({ background: rowBg })}>
                      {carrierBidValue != null ? carrierBidValue : "-"}
                    </td>
                  )}
                  {selectedColumns.includes("Loads") && (
                    <td style={cellStyle({ background: rowBg })}>
                      {carrierLoads != null ? carrierLoads : "-"}
                    </td>
                  )}
                  {selectedColumns.includes("EstimatedTransitDays") && (
                    <td style={cellStyle({ background: rowBg })}>
                      {carrierEtd != null ? carrierEtd : "-"}
                    </td>
                  )}
                  {selectedColumns.includes("SelectQuote") && (
                    <td style={cellStyle()}>
                      <MCheckBox
                        label={null}
                        checked={!!accepted}
                        disabled={disabled}
                        tooltipName={tooltip}
                        noPadding={true}
                        fontSize={"22px"}
                        onChange={(e) => {
                          const nextAccepted = !!e?.target?.checked;

                          if (!quoteSelectionEnabled) return;
                          if (!quoteId) return;

                          setQuoteAccepted({ quoteId, nextAccepted });
                        }}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="carrier-response-detail-paginator">
        {/* Paginator controlling the number of lanes displayed and the current page */}
        <Paginator
          first={first}
          rows={rows}
          totalRecords={filteredLanes.length}
          rowsPerPageOptions={[10, 20, 50]}
          onPageChange={(e) => {
            setFirst(e.first);
            setRows(e.rows);
          }}
          template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        />
      </div>
    </div>
  );
}

CarrierResponseDetailTable.propTypes = {
  tenderInfo: PropTypes.object,
  selectedCarrier: PropTypes.number,
  selectedPhaseId: PropTypes.number,
  quotes: PropTypes.array,
  lanes: PropTypes.array,
  isSpotTender: PropTypes.bool,
  onRefresh: PropTypes.func,
  bestMetric: PropTypes.oneOf(["bid", "loads", "etd"]),

  laneBestMap: PropTypes.instanceOf(Map),
  acceptBusyIds: PropTypes.oneOfType([
    PropTypes.instanceOf(Set),
    PropTypes.arrayOf(PropTypes.number),
  ]),
  onSetQuoteAccepted: PropTypes.func,
};
